import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type TireQuoteRecordCustomer, type TireQuoteRejectionReason } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiButton, NoddiSheet, NoddiTextArea, useIsMobile } from 'noddi-ui-web';
import { commonTrackingEvents, tracking } from 'noddi-web';
import { useState } from 'react';
import { ApiErrorMessageWithTrans } from '../../Error/ApiErrorMessageWithTrans';
import { RejectionReasonPicker } from '../RejectionReasonPicker';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offer: TireQuoteRecordCustomer;
  onRemindInstead: () => void;
};

export const TireOfferDeclineSheet = ({ open, onOpenChange, offer, onRemindInstead }: Props) => {
  const isMobile = useIsMobile();
  const [reasons, setReasons] = useState<TireQuoteRejectionReason[]>([]);
  const [comments, setComments] = useState('');
  const { mutate, isPending, isSuccess, error } = noddiAsync.usePost({
    type: URLKeys.postRejectTireOfferQuote,
    queryConfig: {
      onSuccess: () => {
        void invalidateQueryExactMatch({
          urlKey: URLKeys.getCarTireQuoteBySlug,
          input: { carTireQuoteSlug: offer.slug }
        });
      }
    }
  });
  const canRemind = (offer.reminderOptions ?? []).length > 0;

  const confirm = () =>
    mutate(
      { carTireQuoteId: offer.id, reasons, comments: comments.trim() },
      {
        onSuccess: () =>
          tracking.track(commonTrackingEvents.tireOfferDeclined, {
            slug: offer.slug,
            reasons,
            hasComment: comments.trim().length > 0
          })
      }
    );

  return (
    <NoddiSheet
      open={open}
      onOpenChange={onOpenChange}
      side='bottom'
      className={isMobile ? 'h-[85dvh]' : undefined}
      title={t`Why are you not interested?`}
      description={t`Your answer helps us make better offers. Pick what fits.`}
      isSuccess={isSuccess}
      successTitle={t`Thanks for your answer`}
      successDescription={t`We will not send more reminders about this offer. We measure your tires again at your next tire change.`}
      footer={
        !isSuccess && (
          <>
            <NoddiButton className='w-full' onClick={confirm} loading={isPending}>
              <Trans>Send answer</Trans>
            </NoddiButton>
            {canRemind && (
              <NoddiButton variant='ghost' className='w-full' onClick={onRemindInstead}>
                <Trans>Remind me later instead</Trans>
              </NoddiButton>
            )}
          </>
        )
      }
    >
      <div className='flex flex-col gap-4'>
        <RejectionReasonPicker value={reasons} onChange={setReasons} />
        <NoddiTextArea
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          placeholder={t`Want to tell us more? (optional)`}
          maxLength={2000}
        />
        {error && <ApiErrorMessageWithTrans error={error} />}
      </div>
    </NoddiSheet>
  );
};
